import Fade from "@mui/material/Fade";
import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";

// react-router-dom components
import { useLocation } from "react-router-dom";

// prop-types is a library for typechecking of props.

// @mui material components

// Argon Dashboard 2 PRO MUI components
import { BusinessProvider } from "0.Mine/2.Context/businessContext";
import ArgonBox from "components/ArgonBox";

// Argon Dashboard 2 PRO MUI context
import { useArgonController, setLayout } from "context";

function DashboardLayout({
  bgColor,
  children,
  isFullscreen = false,
  disableAnimation = false,
  ...rest
}) {
  const [controller, dispatch] = useArgonController();
  const { miniSidenav, darkMode } = controller;
  const { pathname } = useLocation();

  // State to control content visibility and animation
  const [contentReady, setContentReady] = useState(disableAnimation ? true : false);

  useEffect(() => {
    // If fullscreen, use a different layout type to avoid dashboard elements
    setLayout(dispatch, isFullscreen ? "fullscreen" : "dashboard");

    // Only manage animation if not disabled
    if (!disableAnimation) {
      // Reset content visibility on route change
      setContentReady(false);

      // Delay showing content for a smooth fade-in effect
      const timer = setTimeout(() => {
        setContentReady(true);
      }, 150); // Small delay for smooth transition

      return () => clearTimeout(timer);
    }
  }, [pathname, isFullscreen, dispatch, disableAnimation]);

  const background = darkMode && !bgColor ? "transparent" : bgColor;

  // Render content with or without animation
  const renderContent = () => {
    // If animation is disabled, render content directly
    if (disableAnimation) {
      return <BusinessProvider>{children}</BusinessProvider>;
    }

    // Otherwise, use fade-in animation
    return (
      <Fade in={contentReady} timeout={600}>
        <div
          style={{
            height: "100%",
            transform: contentReady ? "translateY(0)" : "translateY(15px)",
            transition: "transform 600ms cubic-bezier(0.4, 0, 0.2, 1)",
            // This ensures the div doesn't interfere with pointer events
            pointerEvents: contentReady ? "auto" : "none",
          }}
        >
          <BusinessProvider>{children}</BusinessProvider>
        </div>
      </Fade>
    );
  };

  // If fullscreen mode is enabled, render a clean fullscreen container
  if (isFullscreen) {
    return (
      <ArgonBox
        sx={{
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0,
          overflow: "hidden",
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          bgcolor: background || "#fff",
          zIndex: 0,
        }}
      >
        {renderContent()}
      </ArgonBox>
    );
  }

  // Otherwise, render the regular dashboard layout
  return (
    <ArgonBox
      sx={({ breakpoints, transitions, functions: { pxToRem } }) => ({
        p: 3,
        height: "100%",

        [breakpoints.up("xl")]: {
          marginLeft: miniSidenav ? pxToRem(120) : pxToRem(274),
          transition: transitions.create(["margin-left", "margin-right"], {
            easing: transitions.easing.easeInOut,
            duration: transitions.duration.standard,
          }),
        },
      })}
    >
      <ArgonBox
        height="300px"
        width="100vw"
        position="absolute"
        top={0}
        left={0}
        sx={{
          backgroundColor: "transparent", // sfondo completamente rimosso
          pointerEvents: "none", // opzionale: rende il box non cliccabile
          zIndex: 0, // opzionale: lo tiene in basso
        }}
        {...rest}
      />

      {renderContent()}
    </ArgonBox>
  );
}

// Typechecking props for the DashboardLayout
DashboardLayout.propTypes = {
  bgColor: PropTypes.string,
  children: PropTypes.node.isRequired,
  isFullscreen: PropTypes.bool,
  disableAnimation: PropTypes.bool, // Add this prop
};

export default React.memo(DashboardLayout);
